import { App as CapacitorApp } from "@capacitor/app";
import { useEffect, useRef, type RefObject } from "react";

let nativeBackPending = false;

export function useModalFocus<T extends HTMLElement = HTMLDivElement>(onClose: () => void, returnFocus?: RefObject<HTMLElement | null>) {
  const ref = useRef<T>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const panel = ref.current;
    if (!panel) return;
    const overlayId = crypto.randomUUID();
    let closedByHistory = false;
    let disposed = false;
    let nativeListener: Awaited<ReturnType<typeof CapacitorApp.addListener>> | null = null;
    const previous = returnFocus?.current ?? document.activeElement;
    const focusable = (): HTMLElement[] => Array.from(panel.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex="0"]',
    ));
    (focusable()[0] ?? panel).focus();
    const keydown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.preventDefault();
        close.current();
      }
      if (event.key !== "Tab") return;
      const elements = focusable();
      const first = elements[0] ?? panel;
      const last = elements.at(-1) ?? panel;
      if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === panel)) {
        event.preventDefault();
        first.focus();
      }
    };
    const popstate = (event: PopStateEvent): void => {
      if (event.state?.bumaOverlayId === overlayId) return;
      closedByHistory = true;
      close.current();
    };

    window.history.pushState({ ...window.history.state, bumaOverlayId: overlayId }, "");
    window.addEventListener("popstate", popstate);
    void CapacitorApp.addListener("backButton", () => {
      if (window.history.state?.bumaOverlayId !== overlayId || nativeBackPending) return;
      nativeBackPending = true;
      window.history.back();
      window.setTimeout(() => { nativeBackPending = false; }, 250);
    }).then((listener) => {
      if (disposed) void listener.remove();
      else nativeListener = listener;
    });
    panel.addEventListener("keydown", keydown);
    return () => {
      disposed = true;
      panel.removeEventListener("keydown", keydown);
      window.removeEventListener("popstate", popstate);
      void nativeListener?.remove();
      if (!closedByHistory && window.history.state?.bumaOverlayId === overlayId) {
        window.history.back();
      }
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, [returnFocus]);
  return ref;
}
